import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import type {
  CreateUploadRequestsResult,
  UploadLinkConfig,
  UploadRequestTargetType,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { EmailAddressInput } from '../messaging/EmailAddressInput';
import { MessageComposer } from '../messaging/MessageComposer';
import { UploadLinkFields } from '../messaging/UploadLinkOptions';
import { fetchRecordFolders } from '../../api/threadTools';
import { useCreateUploadRequests } from '../../hooks/useUploadRequests';
import { extractApiErrorMessage } from '../../utils/apiError';

/** Somebody the record already knows, offered with a tick. */
export interface RequestFilesSuggestion {
  email: string;
  name?: string | null;
  /** What they are to the record — "Client", "Vendor · Bella Catering". */
  description?: string;
}

/** The record the files are about, as a caller hands it to the modal. */
export interface RequestFilesTarget {
  entityType: UploadRequestTargetType;
  entityId: string;
  /** Named in the dialog, so the producer sees what they are asking about. */
  recordName?: string;
  suggestedRecipients?: RequestFilesSuggestion[];
  /**
   * The record whose library folders may be chosen, in the conversation
   * spelling (`Event`) — `/threads/record-folders` walks from it. Without one
   * the file goes on the record only.
   */
  folderScope?: { entityType: string; entityId: string };
}

export interface RequestFilesModalProps extends RequestFilesTarget {
  open: boolean;
  onClose: () => void;
}

const NOTE_MAX_LENGTH = 2000;

/**
 * "Request files": ask one or more people to send files about a record, each
 * through a link of their own (Phase 9). The email goes from the server; the
 * links come back as well, because an address that bounced or a vendor who
 * reads WhatsApp before email still needs a way to be sent theirs.
 *
 * ⚠️ **One link per person, never one for everybody.** A link is single-use:
 * shared, it is spent by whoever answers first and the file is credited to
 * them. So the result lists each person with their own Copy button rather than
 * offering "the" link.
 */
export function RequestFilesModal({
  open,
  onClose,
  entityType,
  entityId,
  recordName,
  suggestedRecipients = [],
  folderScope,
}: RequestFilesModalProps) {
  return (
    <EntityModal open={open} onClose={onClose} title="Request files" size="md">
      {/* Remounted on every opening, so a second request starts empty. */}
      {open && (
        <RequestFilesBody
          onClose={onClose}
          entityType={entityType}
          entityId={entityId}
          recordName={recordName}
          suggestedRecipients={suggestedRecipients}
          folderScope={folderScope}
        />
      )}
    </EntityModal>
  );
}

function RequestFilesBody({
  onClose,
  entityType,
  entityId,
  recordName,
  suggestedRecipients = [],
  folderScope,
}: RequestFilesTarget & { onClose: () => void }) {
  const createRequests = useCreateUploadRequests();
  // Each address once, whatever the caller's list repeats.
  const suggestions = useMemo(() => {
    const seen = new Set<string>();
    return suggestedRecipients.filter((person) => {
      const key = person.email?.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [suggestedRecipients]);
  // The one person a record offers is almost always who is meant.
  const [ticked, setTicked] = useState<Set<string>>(() =>
    suggestions.length === 1 ? new Set([suggestions[0].email.toLowerCase()]) : new Set(),
  );
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [config, setConfig] = useState<UploadLinkConfig>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateUploadRequestsResult | null>(null);

  /*
   * The record's library folders, the list a conversation about it offers —
   * fetched exactly as the vendor email does. Only real folders: the record's
   * own node is what choosing nothing already means.
   */
  const { data: recordFolders, isLoading: foldersLoading } = useQuery({
    queryKey: ['record-folders', folderScope?.entityType, folderScope?.entityId],
    queryFn: () => fetchRecordFolders(folderScope ?? null),
    enabled: !!folderScope,
  });
  const folders = useMemo(
    () =>
      (recordFolders ?? []).flatMap((folder) =>
        folder.nodeId.startsWith('folder:')
          ? [{ id: folder.nodeId.slice('folder:'.length), path: folder.path }]
          : [],
      ),
    [recordFolders],
  );

  const recipients = useMemo(() => {
    const chosen = suggestions
      .filter((person) => ticked.has(person.email.toLowerCase()))
      .map((person) => ({ email: person.email.trim(), name: person.name ?? null }));
    for (const email of extraEmails) {
      if (!chosen.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
        chosen.push({ email, name: null });
      }
    }
    return chosen;
  }, [suggestions, ticked, extraEmails]);

  function toggle(email: string) {
    const key = email.toLowerCase();
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function send() {
    if (recipients.length === 0) {
      setError('Choose at least one person to ask.');
      return;
    }
    if (!label.trim()) {
      setError('Say what they should send.');
      return;
    }
    setError(null);
    try {
      const answer = await createRequests.mutateAsync({
        entityType,
        entityId,
        recipients,
        message: note.trim() || null,
        uploadLink: { ...config, label: label.trim() },
      });
      setResult(answer);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The request could not be sent.'));
    }
  }

  if (result) {
    const nameOf = (email: string) =>
      recipients.find((r) => r.email.toLowerCase() === email.toLowerCase())?.name ?? null;
    return (
      <div className="space-y-4" data-testid="request-files-result">
        <p className="text-sm text-gray-700">
          {result.sent.length > 0
            ? `Sent. Each person has a link of their own${result.failed.length > 0 ? ', but not every email went' : ''}.`
            : 'The links were made, but no email went. Copy each link and send it yourself.'}
        </p>
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
          {result.sent.map((row) => (
            <RequestLinkRow
              key={row.email}
              email={row.email}
              name={nameOf(row.email)}
              url={row.url}
            />
          ))}
          {result.failed.map((row) => (
            <RequestLinkRow
              key={row.email}
              email={row.email}
              name={nameOf(row.email)}
              url={row.url}
              failedReason={row.reason}
            />
          ))}
        </ul>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 sm:min-h-0 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const busy = createRequests.isPending;
  return (
    <div className="space-y-4">
      {recordName && (
        <p className="text-sm text-gray-600">
          About <span className="font-medium text-gray-900">{recordName}</span>. Each person gets a
          link of their own — no login needed.
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          Who should send them?
        </legend>
        {suggestions.length > 0 && (
          <ul className="space-y-1" data-testid="request-files-suggestions">
            {suggestions.map((person) => (
              <li key={person.email}>
                <label className="flex items-start gap-2 min-h-11 sm:min-h-0 py-1 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ticked.has(person.email.toLowerCase())}
                    onChange={() => toggle(person.email)}
                    disabled={busy}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{person.name || person.email}</span>
                    {person.name && <span className="text-gray-500"> · {person.email}</span>}
                    {person.description && (
                      <span className="block text-xs text-gray-500">{person.description}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <EmailAddressInput
          emails={extraEmails}
          onChange={setExtraEmails}
          onError={setError}
          placeholder={suggestions.length > 0 ? 'Add another email address…' : 'Email address…'}
          disabled={busy}
        />
      </fieldset>

      <div>
        <label
          htmlFor="request-files-label"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          What should they send?
        </label>
        <input
          id="request-files-label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={busy}
          maxLength={200}
          placeholder="Your certificate of insurance"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <MessageComposer
        id="request-files-note"
        label="Note (optional)"
        format="text"
        value={note}
        onChange={setNote}
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        disabled={busy}
        placeholder="Anything they should know — shown above the button in the email."
        record={folderScope}
        onError={setError}
      />

      <UploadLinkFields
        config={config}
        onConfigChange={setConfig}
        // The request is about a record, so a folder only says where the files
        // are *also* filed.
        hasRecordTarget
        folders={folders}
        foldersLoading={!!folderScope && foldersLoading}
        disabled={busy}
      />

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-0 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy}
          className="min-h-11 sm:min-h-0 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          data-testid="request-files-send"
        >
          {busy
            ? 'Sending…'
            : recipients.length > 1
              ? `Send to ${recipients.length} people`
              : 'Send request'}
        </button>
      </div>
    </div>
  );
}

/** One person's link, with a Copy button — and why their email did not go, if it did not. */
function RequestLinkRow({
  email,
  name,
  url,
  failedReason,
}: {
  email: string;
  name: string | null;
  url: string;
  failedReason?: string;
}) {
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);

  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('No clipboard');
      await navigator.clipboard.writeText(url);
      setCopied('yes');
    } catch {
      // Some browsers refuse the clipboard outside a secure context; the link
      // is on screen to select by hand.
      setCopied('no');
    }
  }

  return (
    <li
      className="flex items-center gap-3 px-3 py-2"
      data-testid="request-files-link"
      data-failed={failedReason ? 'true' : undefined}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-800">{name || email}</p>
        {name && <p className="truncate text-xs text-gray-500">{email}</p>}
        {failedReason && (
          <p className="flex items-start gap-1 text-xs text-amber-700" title={failedReason}>
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            Email not sent — copy the link instead.
          </p>
        )}
        {copied === 'no' && (
          <p className="break-all text-xs text-gray-600 select-all" data-testid="request-files-url">
            {url}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={`Copy ${name || email}’s link`}
        className="flex items-center gap-1 min-h-11 sm:min-h-0 px-3 py-1.5 text-xs text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 flex-shrink-0"
      >
        {copied === 'yes' ? <Check size={14} /> : <Copy size={14} />}
        {copied === 'yes' ? 'Copied' : 'Copy link'}
      </button>
    </li>
  );
}
